import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { ExtraLocationMapEditor } from './ExtraLocationMapEditor';

/**
 * Additional-charts section that renders inline under each primary ChartBuilder
 * (stall or RV). Each entry is stored in the existing `extra_locations` backend
 * collection so we get the full map-editor feature set for free.
 *
 * Behavior:
 *  - Filters backend extra_locations to match the given `type` prop (stall/rv)
 *  - Lets producers add a new chart (name + image upload via the editor)
 *  - Each chart gets the full primary-chart treatment:
 *      • Add barns + rows + spots + start_number (matching ChartBuilder UX)
 *      • Map editor with region drawing + auto-detect + per-barn floor plans
 *      • Save chart to `/extra-locations/{id}` (chart + markers)
 *  - Independent numbering: stall #1 in Chart 1 and Chart 2 are different.
 */
export const AdditionalChartsSection = ({ showId, type = 'stall' }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/shows/${showId}/spot-map`);
      // spot-map returns `map: [{rows: [{label, spots: [{number,status}...]}]}]`
      // where `spots` is an ARRAY of spot objects. Convert to row-shape that
      // the editor expects (matches ChartBuilder.loadChart: `spots: r.spots.length`).
      const raw = (data.extra_locations || []).filter(l => l.type === type);
      setLocations(raw.map(l => ({
        ...l,
        chart: (l.map || l.chart || []).map(b => ({
          barn: b.barn || 'Untitled',
          detail_map_url: b.detail_map_url || null,
          detail_markers: b.detail_markers || [],
          rows: (b.rows || []).map(r => ({
            label: r.label,
            spots: Array.isArray(r.spots) ? r.spots.length : (Number(r.spots) || 0),
            start_number: r.start_number || 1,
            custom_labels: r.custom_labels || {},
          })),
        })),
      })));
    } catch {
      /* silent — load also runs after add/delete */
    } finally {
      setLoading(false);
    }
  }, [showId, type]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) { toast.error('Enter a chart name'); return; }
    setAdding(true);
    try {
      await api.post(`/shows/${showId}/extra-locations`, { name, type });
      toast.success(`Added "${name}"`);
      setNewName('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add chart');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (locId, name) => {
    if (!window.confirm(`Delete chart "${name}"? All its stalls and reservations will be removed.`)) return;
    try {
      await api.delete(`/shows/${showId}/extra-locations/${locId}`);
      toast.success('Chart deleted');
      await load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const label = type === 'rv' ? 'RV Chart' : 'Stall Chart';

  if (loading) return null;

  return (
    <div className="mt-4 space-y-3" data-testid={`${type}-additional-charts`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h4 className="text-sm font-bold text-[#F8F8F8]" style={{ fontFamily: 'Outfit' }}>
            Additional {label}s
            {locations.length > 0 && <span className="ml-2 text-xs text-[#9E9E9E] font-normal">{locations.length} extra</span>}
          </h4>
          <p className="text-[10px] text-[#9E9E9E] mt-0.5">
            Each additional chart gets independent numbering, its own map, and the same barn/row editor as the main chart.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            placeholder={`e.g. ${type === 'rv' ? 'West RV Lot' : 'Overflow Barns'}`}
            className="px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#0096FF] min-w-[180px]"
            data-testid={`${type}-additional-new-name`}
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={adding || !newName.trim()}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#0096FF] rounded text-xs text-white font-semibold hover:bg-[#00D4FF] disabled:opacity-50"
            data-testid={`${type}-additional-add-btn`}
          >
            <Plus size={12} /> {adding ? 'Adding…' : `Add ${label}`}
          </button>
        </div>
      </div>

      {locations.map((loc) => (
        <AdditionalChartCard
          key={loc.id}
          showId={showId}
          location={loc}
          onDelete={() => handleDelete(loc.id, loc.name)}
          onChanged={load}
        />
      ))}
    </div>
  );
};

/**
 * One additional chart's complete UI: barn-list editor (same shape as
 * ChartBuilder) + map editor with per-barn floor plans + save.
 */
const AdditionalChartCard = ({ showId, location, onDelete, onChanged }) => {
  const [barns, setBarns] = useState(location.chart || []);
  const [saving, setSaving] = useState(false);
  // Producer-toggleable visibility of each barn's row editor — defaults
  // collapsed when a barn has >20 rows so the page stays scannable.
  const [expandedBarnRows, setExpandedBarnRows] = useState({});

  useEffect(() => { setBarns(location.chart || []); }, [location.id, location.chart]);

  const totalSpots = barns.reduce(
    (s, b) => s + (b.rows || []).reduce((s2, r) => s2 + (Number(r.spots) || 0), 0),
    0,
  );

  const addBarn = () => {
    const next = barns.length + 1;
    setBarns([...barns, {
      barn: location.type === 'rv' ? `Section ${next}` : `Barn ${String.fromCharCode(64 + next)}`,
      rows: [{ label: 'A', spots: 10, start_number: 1, custom_labels: {} }],
    }]);
  };
  const updateBarn = (i, patch) => setBarns(barns.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const deleteBarn = (i) => {
    if (!window.confirm(`Delete "${barns[i]?.barn}"? Its stalls will be removed from this chart.`)) return;
    setBarns(barns.filter((_, idx) => idx !== i));
  };
  const addRow = (bi) => {
    const b = barns[bi];
    const next = (b.rows || []).length;
    updateBarn(bi, { rows: [...(b.rows || []), { label: String.fromCharCode(65 + next), spots: 10, start_number: 1, custom_labels: {} }] });
  };
  const updateRow = (bi, ri, patch) => {
    const b = barns[bi];
    updateBarn(bi, { rows: b.rows.map((r, idx) => idx === ri ? { ...r, ...patch } : r) });
  };
  const deleteRow = (bi, ri) => {
    const b = barns[bi];
    updateBarn(bi, { rows: b.rows.filter((_, idx) => idx !== ri) });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Payload normalization mirrors ChartBuilder: spots/start_number → ints.
      const payload = barns.map(b => ({
        barn: (b.barn || '').trim() || 'Untitled',
        detail_map_url: b.detail_map_url || null,
        detail_markers: b.detail_markers || [],
        rows: (b.rows || []).map(r => ({
          label: (r.label || '').trim() || 'A',
          spots: Math.max(0, parseInt(r.spots, 10) || 0),
          start_number: Math.max(1, parseInt(r.start_number, 10) || 1),
          custom_labels: r.custom_labels || {},
        })),
      }));
      await api.put(`/shows/${showId}/extra-locations/${location.id}`, { chart: payload });
      toast.success(`Saved "${location.name}"`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#262626] rounded-lg overflow-hidden" data-testid={`${location.type}-additional-chart-${location.id}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-[#0A0A0A] border-b border-[#262626] flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-white">{location.name}</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${location.type === 'stall' ? 'bg-[#0096FF]/20 text-[#0096FF]' : 'bg-[#FF9500]/20 text-[#FF9500]'}`}>
            {location.type === 'stall' ? 'STALL' : 'RV'}
          </span>
          <span className="text-[11px] text-[#9E9E9E]">{totalSpots} spot{totalSpots === 1 ? '' : 's'} · {barns.length} barn{barns.length === 1 ? '' : 's'}</span>
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="p-1 text-[#FF3B30]/60 hover:text-[#FF3B30]"
          title="Delete this chart"
          data-testid={`${location.type}-additional-delete-${location.id}`}
        >
          <Trash size={14} />
        </button>
      </div>

      {/* Map editor with regions + per-barn floor plans (v81) */}
      <div className="p-3 border-b border-[#262626]">
        <ExtraLocationMapEditor
          showId={showId}
          location={location}
          onChanged={onChanged}
        />
      </div>

      {/* Barn list — same UX as the primary ChartBuilder */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] text-[#9E9E9E] uppercase tracking-wider">Barns · Rows · Spots</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={addBarn}
              className="flex items-center gap-1 px-2 py-1 bg-[#161616] border border-[#262626] rounded text-[10px] text-[#9E9E9E] hover:text-white hover:border-[#0096FF]/50"
              data-testid={`${location.type}-additional-add-barn-${location.id}`}
            >
              <Plus size={10} /> Add Barn
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-3 py-1 bg-[#0096FF] rounded text-[10px] text-white font-bold hover:bg-[#0096FF]/80 disabled:opacity-50"
              data-testid={`${location.type}-additional-save-${location.id}`}
            >
              {saving ? 'Saving…' : 'Save Chart'}
            </button>
          </div>
        </div>

        {barns.length === 0 ? (
          <p className="text-[11px] text-[#555] italic">No barns yet — click "Add Barn" or use Auto-Detect on the map.</p>
        ) : (
          <div className="space-y-2">
            {barns.map((barn, bi) => (
              <div key={bi} className="bg-[#0A0A0A] border border-[#262626] rounded p-2" data-testid={`${location.type}-additional-barn-${location.id}-${bi}`}>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    value={barn.barn || ''}
                    onChange={(e) => updateBarn(bi, { barn: e.target.value })}
                    placeholder="Barn name"
                    className="flex-1 px-2 py-1 bg-[#161616] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#0096FF]"
                    data-testid={`${location.type}-additional-barn-name-${location.id}-${bi}`}
                  />
                  <button
                    type="button"
                    onClick={() => addRow(bi)}
                    className="px-2 py-1 bg-[#161616] border border-[#262626] rounded text-[10px] text-[#9E9E9E] hover:text-white"
                    data-testid={`${location.type}-additional-add-row-${location.id}-${bi}`}
                  >
                    <Plus size={10} className="inline" /> Row
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteBarn(bi)}
                    className="p-1 text-[#FF3B30]/60 hover:text-[#FF3B30]"
                    title="Delete this barn"
                  >
                    <Trash size={12} />
                  </button>
                </div>
                <div className="space-y-1">
                  {(() => {
                    const rows = barn.rows || [];
                    const manyRows = rows.length > 20;
                    const isExpanded = expandedBarnRows[bi] ?? !manyRows;
                    const firstNum = rows[0]?.start_number ?? 1;
                    const lastNum = (rows[rows.length - 1]?.start_number || 1) + Math.max(0, (rows[rows.length - 1]?.spots || 1) - 1);
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => setExpandedBarnRows(s => ({ ...s, [bi]: !isExpanded }))}
                          className="w-full flex items-center justify-between gap-2 px-2 py-1.5 bg-[#0F0F0F] hover:bg-[#161616] rounded text-[11px] text-white border border-[#262626] mb-1"
                          data-testid={`${location.type}-additional-rows-toggle-${location.id}-${bi}`}
                        >
                          <span>
                            <span className="font-semibold">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
                            {rows.length > 0 && <span className="text-[#9E9E9E]"> · #{firstNum} – {lastNum}</span>}
                          </span>
                          <span className="text-[#9E9E9E]">{isExpanded ? 'Hide rows ▾' : 'Edit rows ▸'}</span>
                        </button>
                        {isExpanded && (
                          <div className="space-y-1 max-h-[480px] overflow-y-auto pr-1">
                            {rows.map((row, ri) => (
                    <div key={ri} className="flex items-center gap-2" data-testid={`${location.type}-additional-row-${location.id}-${bi}-${ri}`}>
                      <span className="text-[10px] text-[#555] w-10">Row</span>
                      <input
                        value={row.label || ''}
                        onChange={(e) => updateRow(bi, ri, { label: e.target.value })}
                        placeholder="A"
                        className="w-14 px-2 py-1 bg-[#161616] border border-[#262626] rounded text-white text-xs text-center focus:outline-none focus:border-[#0096FF]"
                      />
                      <span className="text-[10px] text-[#555]">spots</span>
                      <input
                        type="number"
                        min="0"
                        value={row.spots || 0}
                        onChange={(e) => updateRow(bi, ri, { spots: e.target.value })}
                        className="w-16 px-2 py-1 bg-[#161616] border border-[#262626] rounded text-white text-xs text-center focus:outline-none focus:border-[#0096FF]"
                      />
                      <span className="text-[10px] text-[#555]">start#</span>
                      <input
                        type="number"
                        min="1"
                        value={row.start_number || 1}
                        onChange={(e) => updateRow(bi, ri, { start_number: e.target.value })}
                        className="w-16 px-2 py-1 bg-[#161616] border border-[#262626] rounded text-white text-xs text-center focus:outline-none focus:border-[#0096FF]"
                      />
                      <span className="text-[10px] text-[#9E9E9E] flex-1">
                        → {Number(row.start_number) || 1}-{(Number(row.start_number) || 1) + Math.max(0, (Number(row.spots) || 0) - 1)}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteRow(bi, ri)}
                        className="p-1 text-[#FF3B30]/60 hover:text-[#FF3B30]"
                        title="Delete row"
                      >
                        <Trash size={10} />
                      </button>
                    </div>
                  ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdditionalChartsSection;
